import { forwardRef, memo, ReactNode, RefObject, useEffect, useMemo, useState } from 'react';
import { Container } from '../Container';
import { Divider } from '../Divider';
import { ISelectList } from '../Select';
import { Stack } from '../Stack';
import { FooterContainer, FooterSlotStack } from './style';

interface DropdownFooterRenderProps {
    close?: () => void;
}
export interface DropdownFooterProps {
    primary: ReactNode | ((props: DropdownFooterRenderProps) => ReactNode);
    secondary?: ReactNode | ((props: DropdownFooterRenderProps) => ReactNode);
}

export interface WithDropdownFooter {
    footer?: DropdownFooterProps;
}

export interface FooterProps extends DropdownFooterProps, DropdownFooterRenderProps {
    isMobile?: boolean;
    // We need this props to handle the list scroll in the UI
    footerRef?: React.RefObject<HTMLDivElement>;
    listRef?: React.RefObject<HTMLDivElement & ISelectList>;
    virtualized?: boolean;
}

const useListScroll = (listRef?: RefObject<ISelectList>, virtualized?: boolean) => {
    const [hasScroll, setHasScroll] = useState(false);

    useEffect(() => {
        if (virtualized) {
            setHasScroll(true);
            return;
        }

        const container = listRef?.current?.containerRef;
        if (!container) return;

        let rafId: number;

        const checkScroll = () => {
            const drawerContent = container.closest('[data-drawer-content]');
            const hasListScroll = container.scrollHeight > container.clientHeight;
            const hasDrawerScroll = drawerContent ? drawerContent.scrollHeight > drawerContent.clientHeight : false;

            setHasScroll(hasListScroll || hasDrawerScroll);
        };

        const resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(checkScroll);
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
            cancelAnimationFrame(rafId);
        };
    }, [listRef, virtualized]);

    return hasScroll;
};

const renderContent = (content: ReactNode | ((props: DropdownFooterRenderProps) => ReactNode), close: () => void) => {
    if (typeof content === 'function') {
        return content({ close });
    }
    return content;
};

const FooterContent = memo(
    ({
        primarySlot,
        secondarySlot,
        isMobile,
        secondary,
    }: {
        primarySlot: ReactNode;
        secondarySlot: ReactNode;
        isMobile: boolean;
        secondary?: boolean;
    }) => (
        <Stack
            spacing="100"
            itemAlignment="center"
            alignment="end"
            distribution={secondary ? 'space-between' : undefined}
            vertical={isMobile}
        >
            <FooterSlotStack>{secondarySlot}</FooterSlotStack>
            <FooterSlotStack primary>{primarySlot}</FooterSlotStack>
        </Stack>
    )
);

const DropdownFooterComponent = forwardRef<HTMLDivElement, DropdownFooterProps & FooterProps>(
    ({ primary, secondary, close = () => {}, isMobile = false, listRef, virtualized = false }, ref) => {
        const hasScroll = useListScroll(listRef, virtualized);
        const secondarySlot = useMemo(() => (secondary ? renderContent(secondary, close) : null), [secondary, close]);
        const primarySlot = useMemo(() => renderContent(primary, close), [primary, close]);

        return (
            <FooterContainer ref={ref} width="100%">
                {hasScroll ? <Divider /> : null}
                <Container padding="150" boxSizing="border-box">
                    <FooterContent
                        primarySlot={primarySlot}
                        secondarySlot={secondarySlot}
                        isMobile={isMobile}
                        secondary={!!secondary}
                    />
                </Container>
            </FooterContainer>
        );
    }
);

export const DropdownFooter = memo(DropdownFooterComponent);
